import type { CylinderSurface } from '@repo/api-contracts';
import type { CylinderParams, Raster, Rect } from '@repo/warp-engine';
import { useEffect, useMemo, useRef, useState } from 'react';
import { cylinderParamsOf, type PhotoDimensions } from '@/entities/edit-area';
import { rasterToObjectUrl } from '@/shared/lib';
import { type CylinderPreviewRaster, renderCylinderPreview } from './cylinder-preview-raster';

/** 화면에 겹칠 미리보기 이미지. 위치·크기는 사진 px 이다. */
export interface CylinderPreviewImage extends Rect {
  readonly href: string;
}

/** 사진 바로 위에 겹칠 미리보기 층의 입력. 켜져 있고 이미지가 준비됐을 때만 있다. */
export interface CylinderPreviewOverlay {
  readonly image: CylinderPreviewImage;
  /** 저장되는 합성 설정의 multiply(결정 Q10). 마스크가 없는 면은 합성 설정이 없어 false 다. */
  readonly multiply: boolean;
  /** 그 면 마스크의 표시 URL(결정 R2). 없으면 자르지 않는다. */
  readonly maskUrl: string | null;
}

/**
 * 그 면 마스크로 미리보기를 자를 수 있는가(결정 R2·D-K). 'none' 은 합성 설정에 마스크 키가 없음, 'expired' 는 키는 있는데
 * URL 맵(photoUrls)에 표시 URL 이 없음('서버 상태·사진 URL 확인'으로 다시 받는다), 'ready' 는 둘 다 있음이다.
 */
export type CylinderPreviewMaskStatus = 'none' | 'expired' | 'ready';

/** 저장하지 않는 원통 미리보기의 상태와 조작. 파라미터는 초안의 원통 표면에서만 나온다(결정 Q4). */
export interface CylinderPreviewControl {
  /** 면에 원통 표면 하나와 이 색상의 면 사진 크기가 있어야 켤 수 있다. */
  readonly available: boolean;
  readonly enabled: boolean;
  /** 마스크 키·표시 URL 유무. 'ready' 가 아니면 실루엣대로 잘리지 않는다. */
  readonly maskStatus: CylinderPreviewMaskStatus;
  readonly overlay: CylinderPreviewOverlay | null;
  readonly error: string | null;
  /** 마지막 이미지의 워프·인코딩 시간(ms). 허용 시간은 100ms 다. */
  readonly renderMs: number | null;
  readonly toggle: () => void;
}

interface PreviewDependencies {
  readonly encode: (raster: Raster) => Promise<string>;
  readonly revoke: (url: string) => void;
  readonly render: (params: CylinderParams) => CylinderPreviewRaster;
  readonly now: () => number;
}

interface UseCylinderPreviewOptions extends Partial<PreviewDependencies> {
  /** 선택 면의 초안 원통 표면. 면에 표면이 하나가 아니거나 원통이 아니면 undefined 다. */
  readonly surface: CylinderSurface | undefined;
  /** 기준 사진 색상의 그 면 사진 크기. 면 사진이 없으면 undefined 다. */
  readonly photoSize: PhotoDimensions | undefined;
  /** 그 면 합성 설정의 마스크 키(faces[면].composite?.maskKey). 표시 URL 과 따로 받아 만료를 구분한다(결정 D-K). */
  readonly maskKey: string | undefined;
  /** 그 면 마스크의 표시 URL(photoUrls[composite.maskKey]). 키가 있어도 URL 맵에 없으면 undefined 다. */
  readonly maskUrl: string | undefined;
  /** 그 면 합성 설정의 multiply. 합성 설정이 없으면 false 다. */
  readonly multiply: boolean;
}

interface PreviewOutput {
  readonly image: CylinderPreviewImage | null;
  readonly error: string | null;
  readonly renderMs: number | null;
}

const EMPTY_OUTPUT: PreviewOutput = { image: null, error: null, renderMs: null };
const revokeObjectUrl = (url: string): void => URL.revokeObjectURL(url);
const performanceNow = (): number => performance.now();

const failureMessage = (error: unknown): string =>
  error instanceof Error
    ? `미리보기를 만들 수 없습니다: ${error.message}`
    : '미리보기를 만들 수 없습니다.';

const maskStatusOf = (
  maskKey: string | undefined,
  maskUrl: string | undefined,
): CylinderPreviewMaskStatus => {
  if (maskKey === undefined) return 'none';
  return maskUrl === undefined ? 'expired' : 'ready';
};

/** 미리보기가 묶이는 대상. 다른 표면을 고르거나 사진 크기가 다른 색상으로 바꾸면 끈다. */
const previewTargetKey = (
  surface: CylinderSurface | undefined,
  photoSize: PhotoDimensions | undefined,
): string => `${surface?.id ?? ''}:${photoSize?.widthPx ?? ''}x${photoSize?.heightPx ?? ''}`;

/**
 * 저장하지 않는 원통 미리보기. 초안의 원통 표면과 기준 사진 크기를 어댑터(cylinderParamsOf)에 넣어 체크무늬를
 * 원통에 감은 이미지를 만들고 사진 위에 겹친다. 표면이 바뀌면(곡률·사각형 적용) 바로 다시 만든다.
 * 켜짐 여부만 이 훅의 지역 상태라 편집 초안과 저장(product_edit_areas)을 건드리지 않는다. 사진 픽셀은 읽지 않는다.
 */
export const useCylinderPreview = ({
  surface,
  photoSize,
  maskKey,
  maskUrl,
  multiply,
  encode = rasterToObjectUrl,
  revoke = revokeObjectUrl,
  render = renderCylinderPreview,
  now = performanceNow,
}: UseCylinderPreviewOptions): CylinderPreviewControl => {
  const [enabled, setEnabled] = useState(false);
  const targetKey = previewTargetKey(surface, photoSize);
  const [trackedKey, setTrackedKey] = useState(targetKey);
  if (trackedKey !== targetKey) {
    setTrackedKey(targetKey);
    setEnabled(false);
  }
  const available = surface !== undefined && photoSize !== undefined;
  const widthPx = photoSize?.widthPx;
  const heightPx = photoSize?.heightPx;
  // 사진 크기는 값으로 비교한다 — 같은 크기의 다른 색상으로 바꿔도 다시 만들지 않는다.
  const target = useMemo(
    () =>
      enabled && surface !== undefined && widthPx !== undefined && heightPx !== undefined
        ? cylinderParamsOf(surface, { widthPx, heightPx })
        : null,
    [enabled, surface, widthPx, heightPx],
  );
  const output = usePreviewOutput(target, { encode, revoke, render, now });
  return {
    available,
    enabled,
    maskStatus: maskStatusOf(maskKey, maskUrl),
    overlay:
      output.image === null ? null : { image: output.image, multiply, maskUrl: maskUrl ?? null },
    error: output.error,
    renderMs: output.renderMs,
    toggle: () => {
      if (available) setEnabled(!enabled);
    },
  };
};

/**
 * target 이 바뀔 때마다 미리보기 이미지를 다시 만든다. 늦게 끝난 이전 결과는 버리고 그 URL 을 바로 푼다.
 * 이미지를 바꾸거나 지울 때, 그리고 언마운트할 때 보이던 URL 을 푼다.
 */
const usePreviewOutput = (
  target: CylinderParams | null,
  { encode, revoke, render, now }: PreviewDependencies,
): PreviewOutput => {
  const [output, setOutput] = useState<PreviewOutput>(EMPTY_OUTPUT);
  const shownUrl = useRef<string | null>(null);
  const latestRun = useRef(0);
  useEffect(() => {
    latestRun.current += 1;
    const run = latestRun.current;
    const show = (next: PreviewOutput): void => {
      const previous = shownUrl.current;
      if (previous !== null && previous !== next.image?.href) revoke(previous);
      shownUrl.current = next.image?.href ?? null;
      setOutput(next);
    };
    if (target === null) {
      show(EMPTY_OUTPUT);
      return;
    }
    const startedAt = now();
    let preview: CylinderPreviewRaster;
    try {
      preview = render(target);
    } catch (error) {
      show({ ...EMPTY_OUTPUT, error: failureMessage(error) });
      return;
    }
    encode(preview.raster).then(
      (href) => {
        if (run !== latestRun.current) {
          revoke(href);
          return;
        }
        show({ image: { href, ...preview.placement }, error: null, renderMs: now() - startedAt });
      },
      (error: unknown) => {
        if (run === latestRun.current) show({ ...EMPTY_OUTPUT, error: failureMessage(error) });
      },
    );
  }, [target, encode, revoke, render, now]);
  useEffect(
    () => () => {
      latestRun.current += 1;
      if (shownUrl.current !== null) revoke(shownUrl.current);
    },
    [revoke],
  );
  return output;
};
